/**
 * RemakeApplyMediaMode — Container for the Remake Apply Media tab (mid panel).
 *
 * Renders the channel picker, an 8-slot drop-target grid, and the Auto-Fill
 * action with confirm dialog (Q4 decision: existing slots are overwritten).
 *
 * NOTE: This component does NOT host its own DndContext — the DndContext lives
 * one level up (in RemakeApplyOverlay, PR3 #5) so that drag sources in the
 * sidebar (RemakeImagesPanel) and drop-targets here share the same context.
 *
 * Each slot exposes a drop handler via the `data` payload of `useDroppable`.
 * The Overlay's `onDragEnd` reads `over.data.current.onDrop` and forwards the
 * dragged image payload — see PR3 #5 wiring.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDroppable } from '@dnd-kit/core'
import { Plus, Sparkles } from 'lucide-react'
import { useImageSlots } from '@/hooks/useImageSlots'
import { useApplyRemakeImageToSlot } from '@/hooks/useApplyRemakeImageToSlot'
import { useProductChannelSlots } from '@/hooks/useProductChannelSlots'
import { useProductChannelList } from '@/hooks/useProductChannelList'
import { useBulletMappings } from '@/hooks/useBulletMapping'
import type { BulletMapping } from '@/lib/api/integrations-bullet-mapping'
import { RemakeApplyChannelPicker } from './RemakeApplyChannelPicker'
import { pickAutoFillImages } from './remakeImages'

interface RemakeApplyMediaModeProps {
  productId: string
  workspaceIntegrationId: string | null
  remakeId: string | null
}

function hasMapping(mapping: BulletMapping | undefined): boolean {
  if (!mapping) return false
  return Object.values(mapping).some((v) => v != null)
}

interface FilledSlot {
  assetUrl: string
  fromRemake: boolean
}

const SLOT_COUNT = 8

export function RemakeApplyMediaMode({
  productId,
  workspaceIntegrationId,
  remakeId,
}: RemakeApplyMediaModeProps) {
  const { t } = useTranslation()
  const baseChannelList = useProductChannelList(productId)
  const { data: imageSlots } = useImageSlots(remakeId ?? undefined)
  const applyMutation = useApplyRemakeImageToSlot()
  const { data: mappingsResponse } = useBulletMappings(workspaceIntegrationId)
  const channelMappings = mappingsResponse?.bullet_mapping ?? {}

  const channelList = useMemo(
    () =>
      baseChannelList.map((ch) => ({
        ...ch,
        hasBulletMapping: hasMapping(channelMappings[ch.id]),
      })),
    [baseChannelList, channelMappings],
  )

  const [activeChannel, setActiveChannel] = useState<string>('')
  const effectiveChannel = activeChannel || channelList[0]?.id || ''

  // Hydrate filled slots from existing channel-images for the active channel.
  // QueryKey is keyed by channelId, so a channel switch triggers a fresh
  // fetch and the effect below re-seeds local state with the new DB values.
  const { data: existingSlots } = useProductChannelSlots(
    productId,
    effectiveChannel || null,
  )

  const usableImages = useMemo(
    () => pickAutoFillImages(imageSlots),
    [imageSlots],
  )

  // Local fill state. Hydrated from `existingSlots` on mount and on
  // channel-switch. Drop / Auto-Fill update this map optimistically.
  const [filledSlots, setFilledSlots] = useState<Record<number, FilledSlot>>({})

  useEffect(() => {
    if (!existingSlots) return
    const map: Record<number, FilledSlot> = {}
    for (const s of existingSlots) {
      if (s.asset_url) {
        map[s.slot] = { assetUrl: s.asset_url, fromRemake: false }
      }
    }
    setFilledSlots(map)
  }, [existingSlots])

  const handleDropOnSlot = async (
    slot: number,
    payload: { generationId: string; assetUrl: string },
  ) => {
    if (!effectiveChannel || !remakeId) return
    await applyMutation.mutateAsync({
      remakeId,
      generationId: payload.generationId,
      productId,
      channelId: effectiveChannel,
      slot,
    })
    setFilledSlots((s) => ({
      ...s,
      [slot]: { assetUrl: payload.assetUrl, fromRemake: true },
    }))
  }

  const [confirmOpen, setConfirmOpen] = useState(false)
  const filledCount = Object.keys(filledSlots).length
  const overwriteCount = Math.min(usableImages.length, SLOT_COUNT)
  const willOverwrite = Math.min(filledCount, overwriteCount)

  const runAutoFill = async () => {
    if (!remakeId) return
    setConfirmOpen(false)
    const updates: Record<number, FilledSlot> = {}
    for (let i = 0; i < overwriteCount; i++) {
      const img = usableImages[i]
      const slot = i + 1
      try {
        await applyMutation.mutateAsync({
          remakeId,
          generationId: img.generationId,
          productId,
          channelId: effectiveChannel,
          slot,
        })
        updates[slot] = { assetUrl: img.assetUrl, fromRemake: true }
      } catch {
        break
      }
    }
    setFilledSlots((s) => ({ ...s, ...updates }))
  }

  return (
    <main className="flex-1 overflow-y-auto bg-neutral-50 dark:bg-neutral-950">
      <RemakeApplyChannelPicker
        channels={channelList}
        activeId={effectiveChannel}
        onChange={setActiveChannel}
      />
      <div className="px-6 py-4">
        <div className="flex items-center justify-end mb-3">
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={usableImages.length === 0 || !remakeId}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t('remakeApply.media.autoFillButton')}
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3 max-w-3xl">
          {Array.from({ length: SLOT_COUNT }, (_, i) => i + 1).map((slot) => (
            <Slot
              key={slot}
              slot={slot}
              filled={filledSlots[slot]}
              onDrop={handleDropOnSlot}
            />
          ))}
        </div>
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2">
          {t('remakeApply.media.tipDragDrop')}
        </div>
      </div>

      {confirmOpen && (
        <ConfirmAutoFill
          filled={willOverwrite}
          total={overwriteCount}
          onConfirm={runAutoFill}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </main>
  )
}

interface SlotProps {
  slot: number
  filled?: FilledSlot
  onDrop: (
    slot: number,
    payload: { generationId: string; assetUrl: string },
  ) => void
}

function Slot({ slot, filled, onDrop }: SlotProps) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({
    id: `media-slot-${slot}`,
    data: { slot, onDrop },
  })
  return (
    <div
      ref={setNodeRef}
      data-testid={`slot-${slot}`}
      className={[
        'aspect-square rounded-xl overflow-hidden relative border-2 border-dashed flex items-center justify-center text-xs',
        isOver
          ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
          : 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900',
      ].join(' ')}
    >
      {filled ? (
        <>
          <img
            src={filled.assetUrl}
            alt=""
            className="w-full h-full object-cover"
          />
          {filled.fromRemake && (
            <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-2xs font-medium rounded-full">
              {t('remakeApply.media.fromRemakeBadge')}
            </div>
          )}
        </>
      ) : (
        <div className="text-neutral-400 text-center">
          <Plus className="w-5 h-5 mx-auto mb-1" />
          <div>{t('remakeApply.media.slotLabel', { n: slot })}</div>
        </div>
      )}
    </div>
  )
}

interface ConfirmAutoFillProps {
  filled: number
  total: number
  onConfirm: () => void
  onCancel: () => void
}

function ConfirmAutoFill({
  filled,
  total,
  onConfirm,
  onCancel,
}: ConfirmAutoFillProps) {
  const { t } = useTranslation()
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
    >
      <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-xl max-w-md w-full p-5 space-y-3">
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
          {t('remakeApply.media.autoFillConfirmTitle')}
        </h3>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {t('remakeApply.media.autoFillConfirmBody', { filled, total })}
        </p>
        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            {t('remakeApply.media.autoFillCancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600"
          >
            {t('remakeApply.media.autoFillConfirmAction')}
          </button>
        </div>
      </div>
    </div>
  )
}
